import { Shield, Wifi, Zap, MapPin, Clock, Droplets, Gamepad2 } from "lucide-react";
import { motion } from "motion/react";

// Custom Ghana Cedi Icon Component
const CediIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M16 8c-2.2-2-4.8-3-8-3v14c3.2 0 5.8-1 8-3" />
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
  </svg>
);

const features = [
  {
    icon: Shield,
    title: "Maximum Safety",
    description: "24/7 security with CCTV surveillance and secure access control for your peace of mind",
  },
  {
    icon: CediIcon,
    title: "Affordable Rates",
    description: "Student-friendly pricing with flexible payment plans that won't break the bank",
  },
  {
    icon: Wifi,
    title: "Starlink WiFi",
    description: "High-speed Starlink internet connectivity perfect for studying and streaming",
  },
  {
    icon: Zap,
    title: "24/7 Electricity",
    description: "Uninterrupted power supply with backup generators to keep you connected always",
  },
  {
    icon: MapPin,
    title: "Near Campus",
    description: "Strategically located within walking distance to major universities and colleges",
  },
  {
    icon: Clock,
    title: "24/7 Support",
    description: "Round-the-clock management support to address all your needs and concerns",
  },
  {
    icon: Droplets,
    title: "24/7 Water Supply",
    description: "Constant water supply with backup storage tanks ensuring you never run out",
  },
  {
    icon: Gamepad2,
    title: "Games (Snooker/Table Soccer)",
    description: "Recreation facilities including snooker and table soccer for your leisure time",
  },
];

export function WhyChooseUs() {
  return (
    <section id="facilities" className="py-24 bg-white relative overflow-hidden" aria-labelledby="facilities-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl mb-16"
        >
          <h2 id="facilities-heading" className="text-[#0f1f3d] mb-4 text-4xl lg:text-5xl">
            Why choose Majesty Hostel
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Everything you need for a comfortable and successful academic journey — built in from day one.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (index % 3) * 0.06, ease: [0.16, 1, 0.3, 1] }}
              className="group rounded-2xl border border-gray-200 bg-white p-8 transition-all duration-300 hover:border-[#d4af37]/30 hover:shadow-xl h-full"
            >
              <div className="w-12 h-12 rounded-xl bg-[#d4af37]/10 flex items-center justify-center mb-6">
                <feature.icon className="w-6 h-6 text-[#d4af37]" aria-hidden="true" />
              </div>
              <h3 className="font-semibold text-[#0f1f3d] mb-3 text-xl">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
